import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  ReplenishNowRequest,
  StockReplenishmentPanel,
} from "@shared-core/data/api-types";
import {
  RAISES_AS_LABELS,
  parsePoundsToPence,
} from "@shared-core/domain/stock-replenishment";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Input } from "../../app/components/primitives/input";
import { RaisePreviewCard, useRaisePreview } from "./RaisePreviewCard";
import {
  FieldShell,
  type FieldErrorMap,
  toFieldErrorMap,
} from "./StockFormField";

/** Raise the item's reprint at once, for a quantity the person chooses, after seeing what it would do. */
export function ReplenishNowDialog({
  panel,
  onClose,
  onRaised,
}: {
  panel: StockReplenishmentPanel;
  onClose: () => void;
  onRaised: (quantity: number) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [quantity, setQuantity] = useState(
    String(panel.reorder_quantity ?? ""),
  );
  const [price, setPrice] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const typedPrice = price.trim();
  const pence = typedPrice ? parsePoundsToPence(typedPrice) : null;
  const priceError =
    typedPrice && pence == null
      ? "Enter the price in pounds, like 545.00."
      : undefined;
  const request: ReplenishNowRequest & { quantity: number } = {
    quantity: Number(quantity),
    ...(pence != null ? { price_pence: pence } : {}),
    ...(orderNumber.trim()
      ? { customer_order_number: orderNumber.trim() }
      : {}),
  };
  const preview = useRaisePreview(
    { kind: "item", stockItemId: panel.stock_item_id },
    quantity.trim() === "" ? null : request,
  );
  const lead = preview.preview?.items[0];
  const previewMessages: FieldErrorMap = {};
  for (const blocker of lead?.blockers ?? []) {
    if (blocker.field !== "stock_item_id") {
      previewMessages[blocker.field] ??= blocker.message;
    }
  }
  const messageFor = (field: string) =>
    fieldErrors[field] ?? previewMessages[field];
  const needsPrice =
    lead?.needs_reason === "price_needed" || fieldErrors.price_pence != null;
  const needsOrderNumber =
    lead?.needs_reason === "po_number_needed" ||
    fieldErrors.customer_order_number != null;
  const cannotRaise = preview.status === "ready" && !preview.preview.can_raise;

  async function handleRaise() {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await dataAdapter.replenishNow(panel.stock_item_id, request);
      onRaised(request.quantity);
    } catch (error) {
      console.error("stock.replenishment.replenish_now_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : error instanceof ApiError &&
              (error.status === 409 || error.status === 503)
            ? error.detail
            : "Couldn't raise the reprint. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Replenish now</DialogTitle>
          <DialogDescription>
            {panel.stock_code} · raises as{" "}
            {panel.raises_as
              ? RAISES_AS_LABELS[panel.raises_as].toLowerCase()
              : "an order"}
            , delivered into {panel.delivery_warehouse_name ?? "its warehouse"}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}
          <FieldShell
            id="replenish-now-quantity"
            label="Quantity"
            error={messageFor("quantity")}
            hint="Starts at the re-order quantity."
          >
            {(aria) => (
              <Input
                id="replenish-now-quantity"
                type="number"
                min={1}
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                {...aria}
              />
            )}
          </FieldShell>
          {needsPrice && (
            <FieldShell
              id="replenish-now-price"
              label="Price to bill, ex VAT"
              error={messageFor("price_pence") ?? priceError}
              hint="Recorded as the price a person gave. Only asked for when there is no price group."
            >
              {(aria) => (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">£</span>
                  <Input
                    id="replenish-now-price"
                    inputMode="decimal"
                    placeholder="e.g. 545.00"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    {...aria}
                  />
                </div>
              )}
            </FieldShell>
          )}
          {needsOrderNumber && (
            <FieldShell
              id="replenish-now-order-number"
              label="Customer order number"
              error={messageFor("customer_order_number")}
              hint="This customer's account needs an order number before an order is raised."
            >
              {(aria) => (
                <Input
                  id="replenish-now-order-number"
                  placeholder="e.g. 4500001234"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  {...aria}
                />
              )}
            </FieldShell>
          )}
          <RaisePreviewCard state={preview} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleRaise()}
            disabled={
              isSaving || !quantity || cannotRaise || priceError != null
            }
          >
            {isSaving ? "Raising…" : "Raise reprint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
